import React, { useRef, useEffect } from 'react';
import '../../index.scss';

interface VoiceVisualizerProps {
  /** 是否激活 */
  isActive: boolean;
  /** 是否加载中 */
  isLoading: boolean;
  /** 画布高度（px） */
  height?: number;
}

const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({
  isActive,
  isLoading,
  height = 18,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode>();
  const audioContextRef = useRef<AudioContext>();
  const streamRef = useRef<MediaStream>();
  const phaseRef = useRef(0);
  // 随机因子只生成一次
  const randomFactorsRef = useRef<number[]>([]);


  // 可视化参数（与 VoiceVisualizer 保持一致）
  const barCount = 140;    // 柱形数量
  const barWidth = 3;      // 柱形宽度
  const barGap = 4.5;      // 柱形间距
  const minHeight = 2.5;   // 最小柱高
  const maxHeight = 26;    // 最大柱高

  // 初始化随机因子
  if (randomFactorsRef.current.length === 0) {
    randomFactorsRef.current = Array(barCount)
      .fill(0)
      .map(() => 0.5 + Math.random() * 0.8);
  }

  // 初始化音频分析器
  const initAudioAnalyzer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      if (audioCtx.state === 'suspended') await audioCtx.resume();

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512; // 提高频率分析精度
      const micSource = audioCtx.createMediaStreamSource(stream);
      micSource.connect(analyser);

      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;
      return true;
    } catch (err) {
      console.error('初始化麦克风失败:', err);
      return false;
    }
  };

  // 获取实时音量（0-1）
  const getVoiceVolume = (): number => {
    if (!analyserRef.current) return 0;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    // 增强型音量计算（侧重人声频段）
    const voiceRangeStart = Math.floor(bufferLength * 0.1); // 100Hz左右
    const voiceRangeEnd = Math.floor(bufferLength * 0.6); // 3000Hz左右
    let voiceVolume = 0;

    for (let i = voiceRangeStart;i < voiceRangeEnd;i++) {
      voiceVolume += dataArray[i];
    }
    voiceVolume = voiceVolume / (voiceRangeEnd - voiceRangeStart) / 255;

    // 动态增益控制
    const dynamicGain = voiceVolume > 0.1
      ? Math.min(4, 1.5 + 8 * voiceVolume)
      : 1;

    return Math.min(1, voiceVolume * dynamicGain);
  };

  // 绘制可视化效果
  const drawVisualization = () => {
    if (!canvasRef.current || !isActive) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置画布尺寸（高清屏适配）
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = (rect.height || height) * dpr;
    ctx.scale(dpr, dpr);

    const canvasWidth = rect.width;
    const canvasHeight = rect.height || height;
    const centerY = canvasHeight / 2;

    // 计算总宽度并居中
    const totalWidth = barCount * (barWidth + barGap) - barGap;
    const startX = (canvasWidth - totalWidth) / 2;

    const centerIndex = barCount / 2;
    const randomFactors = randomFactorsRef.current;

    const drawFrame = () => {
      if (!isActive) {
        cancelAnimationFrame(animationRef.current!);
        return;
      }

      animationRef.current = requestAnimationFrame(drawFrame);
      phaseRef.current += 0.06;

      // 获取实时音量
      const volume = getVoiceVolume();

      // 清空画布
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = 'rgba(60, 123, 255, 0.85)';

      // 绘制所有柱子
      for (let i = 0;i < barCount;i++) {
        const x = startX + i * (barWidth + barGap);

        // 计算到中心的距离（0-1）
        const distFromCenter = Math.abs(i - centerIndex) / centerIndex;
        
        // 中间高两边低的包络线（使用余弦曲线）
        const envelope = Math.cos(distFromCenter * Math.PI * 0.5);
        
        // 多层波形叠加
        const wave1 = Math.sin(phaseRef.current * 1.1 + i * 0.12) * 0.4;
        const wave2 = Math.sin(phaseRef.current * 0.7 + i * 0.18) * 0.32;
        const wave3 = Math.sin(phaseRef.current * 1.4 + i * 0.09) * 0.24;
        
        // 基础振幅 + 波形 + 包络 + 音量响应
        const baseAmplitude = 0.6 + wave1 + wave2 + wave3;
        // 音量会增强波形幅度
        const volumeBoost = 1 + volume * 1.9;
        const amplitude = envelope * baseAmplitude * volumeBoost * randomFactors[i] * 1.1;
        const normalizedAmp = Math.max(0.1, Math.min(1, amplitude));
        
        const barHeight = minHeight + normalizedAmp * (maxHeight - minHeight);

        // 绘制圆角柱形
        const radius = barWidth / 2;
        const y = centerY - barHeight / 2;

        ctx.beginPath();
        // 检查浏览器是否支持 roundRect，如果不支持则回退到 fillRect 或自行实现
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, y, barWidth, barHeight, radius);
        } else {
          // 简单的回退：圆角矩形模拟或直接矩形
          // 这里为了简单和性能，如果不支持 roundRect，使用普通矩形
          // 或者使用 arc 来模拟圆角太复杂，这里暂时使用 rect
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }
    };

    drawFrame();
  };

  const cleanup = () => {
    cancelAnimationFrame(animationRef.current!);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current?.close();
    }
  };

  useEffect(() => {
    if (isActive) {
      initAudioAnalyzer().then((success) => {
        if (success) drawVisualization();
      });
    } else {
      cleanup();
    }

    return () => cleanup();
  }, [isActive]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: `${height}px`,
        display: 'block',
        background: 'transparent',
        opacity: isLoading ? 0.5 : 1,
      }}
    />
  );
};

export default VoiceVisualizer;
